import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../shared/useFetchMoreCallback';
import { questionnaireSectionsQuery, questionnaireSectionsQueryVariables } from '../generated/questionnaireSectionsQuery';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { questionnaireSectionFieldsFragment } from '../models/QuestionnaireSection';

export interface QuestionnaireSectionsAsyncLoadPageOptions extends AsyncLoadPagedOptions {
    questionnaireId?: string | null | undefined,
    questionnaireIds?: string[] | null | undefined,
}

/**
 * Get a list of questionnaireSections from the store.
 * @param id
 * @param options
 */
export function useQuestionnaireSections(options: QuestionnaireSectionsAsyncLoadPageOptions = {}): AsyncLoadPagedResult<questionnaireSectionsQuery> {
    // Query data we need from the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<questionnaireSectionsQuery, questionnaireSectionsQueryVariables>(
        gql`
        query questionnaireSectionsQuery ($offset: Int, $first: Int, $questionnaireId: ID, $questionnaireIds: [ID]) {
            items: questionnaireSections (offset: $offset, first: $first, questionnaireId: $questionnaireId, questionnaireIds: $questionnaireIds) {
                ...questionnaireSectionFields
            }
        }

        ${questionnaireSectionFieldsFragment}
        `,
        {

            variables: {
                offset: options.startOffset,
                first: options.pageSize,
                questionnaireId: options.questionnaireId,
                questionnaireIds: options.questionnaireIds,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<questionnaireSectionsQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: [...(prevResults?.items ?? []), ...(newResults?.items ?? [])]
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);
    
    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}
